// src/components/MeetingNotificationsListener.jsx
import { useEffect } from "react";
import { useSocket } from "../contexts/SocketContext";
import { useNotifications } from "../stores/NotificationsStore";
// import { useNavigate } from "react-router-dom";
import { useRtcStore } from "../stores/rtcStore";

const RTCNotificationsListener = () => {
  const { socket } = useSocket();
  const notifications = useNotifications();
  const setMeetingStatus = useRtcStore((s) => s.setMeetingStatus);
  const setCallStatus = useRtcStore((s) => s.setCallStatus);
  const setActiveCall = useRtcStore((s) => s.setActiveCall);
  const clearActiveCall = useRtcStore((s) => s.clearActiveCall);
  const activeCall = useRtcStore((s) => s.activeCall);
  const openOverlay = useRtcStore((s) => s.openOverlay);

  useEffect(() => {
    if (!socket) return;

    // ---------------------------
    // meeting: join request (host 端收到)
    // ---------------------------
    const handleJoinRequest = (payload) => {
      if (payload.kind !== "meeting") return;

      console.log("收到 rtc:join_request:", payload);

      const notificationId = `meeting_join_${payload.id}_${payload.from_user_id}_${Date.now()}`;

      notifications.add({
        id: notificationId,
        type: "meeting_join_request",
        title: "會議加入請求",
        message: `${payload.from_user_name} 想加入會議「${payload.meeting_case_name}」`,
        actions: [
          {
            label: "同意",
            onClick: () => {
              socket.emit("rtc:approve_join", {
                kind: "meeting",
                id: payload.id, // meeting_id
                user_id: payload.from_user_id,
              });
            },
          },
          {
            label: "拒絕",
            onClick: () => {
              socket.emit("rtc:reject_join", {
                kind: "meeting",
                id: payload.id, // meeting_id
                user_id: payload.from_user_id,
              });
            },
          },
        ],
      });
    };

    // ---------------------------
    // private call: incoming (被叫端收到)
    // ---------------------------
    const handleIncoming = (payload) => {
      if (payload.kind !== "private_call") return;

      console.log("收到 rtc:incoming:", payload);

      const notificationId = `call_${payload.id}`;

      notifications.add({
        id: notificationId,
        type: "private_call_incoming",
        title: payload.mode === "video" ? "視訊來電" : "語音來電",
        message: `${payload.from_user_name} ${payload.message || "來電"}`,
        actions: [
          {
            label: "接聽",
            keepOpen: true,     // ✅ 重點：不要自動移除，因為你要 update 成「通話中」
            onClick: async () => {
              // 1) accept
              const res = await fetch(`http://localhost:5000/api/private-calls/${payload.id}/accept`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
              });
              const data = await res.json();
              if (!res.ok) {
                alert(data.error || "接聽失敗");
                return;
              }

              // 2) get token（共用）
              const tokenRes = await fetch(`http://localhost:5000/api/rtc/token`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ kind: "private_call", id: payload.id }),
              });
              const tokenData = await tokenRes.json();
              if (!tokenRes.ok) {
                alert(tokenData.error || "取得 token 失敗");
                return;
              }

              // 3) TODO: 進入 CallRoom UI
              // setCallInfo({ token: tokenData.token, wsUrl: tokenData.ws_url, roomName: tokenData.room_name, mode: payload.mode })

              // ✅ 3) 設定 activeCall（讓全站 CallOverlay/CallRoom 出現）
              const peerName = payload.from_user_name || "對方";
              setActiveCall({
                kind: "private_call",
                id: payload.id,
                peerId: payload.from_user_id,   // 👈 來電者
                mode: payload.mode || "audio",
                peerName,
                status: "accepted",
                token: tokenData.token,
                wsUrl: tokenData.ws_url,
                roomName: tokenData.room_name,
              });
              // setCallStatus(payload.id, "accepted");


              // 可選：把通知更新成「通話中」
              // 你在檔案上面已經有：const openOverlay = useRtcStore((s) => s.openOverlay);

              if (notifications.update) {
                notifications.update(notificationId, {
                  type: "private_call_in_progress",
                  title: "通話中",
                  message: `與 ${peerName} 通話中`,
                  sticky: true, // ✅ 讓被叫端「通話中」也會一直顯示
                  actions: [
                    {
                      label: "展開",
                      keepOpen: true,
                      onClick: () => {
                        openOverlay(); // ✅ 點展開才 render CallOverlay
                      },
                    },
                    {
                      label: "掛斷",
                      onClick: async () => {
                        await fetch(`http://localhost:5000/api/private-calls/${payload.id}/end`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                        });
                        notifications.remove(notificationId);
                        clearActiveCall();
                      },
                    },
                  ],
                });

                // （可選）接聽後直接幫你開 overlay
                // openOverlay();
              } else {
                notifications.remove(notificationId);
              }

            },
          },
          {
            label: "拒絕",
            onClick: async () => {
              await fetch(`http://localhost:5000/api/private-calls/${payload.id}/reject`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
              });
              notifications.remove(notificationId);
            },
          },
        ],
      });
    };

    // ---------------------------
    // rtc accepted (統一：meeting/private_call)
    // ---------------------------
    const handleAccepted = async (payload) => {
      console.log("收到 rtc:accepted:", payload);
      if (!payload?.kind || !payload?.id) return;

      if (payload.kind === "private_call") {
        // 對方已接聽 → 主叫拿 token 進房
        const tokenRes = await fetch(`http://localhost:5000/api/rtc/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ kind: "private_call", id: payload.id }),
        });
        const tokenData = await tokenRes.json();
        if (!tokenRes.ok) {
          alert(tokenData.error || "取得 token 失敗");
          return;
        }
        // TODO: 進入 CallRoom UI
        // setCallInfo(...)
        // setCallStatus(payload.id, "accepted");

        const peerName = activeCall?.peerName || "對方"; // ✅ 不用 undefined 的 peerName
        setActiveCall({
          kind: "private_call",
          id: payload.id,
          peerId: payload.callee_id,   // 👈 對方
          mode: payload.mode || activeCall?.mode || "audio",
          peerName,
          status: "accepted",
          token: tokenData.token,
          wsUrl: tokenData.ws_url,
          roomName: tokenData.room_name,
        });

        // 更新撥號通知（如果你有用 call_<id>）
        const notificationId = `call_${payload.id}`;

        notifications.update(notificationId, {
          type: "call_in_progress",
          title: "通話中",
          message: `與 ${peerName} 通話中`,
          actions: [
            {
              label: "展開",
              keepOpen: true,
              onClick: () => {
                // ✅ 打開通話 UI：用 modal / drawer / overlay
                openOverlay();
                // setActiveCall({ kind: "private_call", id: payload.id, mode: payload.mode });
              },
            },
            {
              label: "掛斷",
              onClick: async () => {
                await fetch(`http://localhost:5000/api/private-calls/${payload.id}/end`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                });
                notifications.remove(notificationId);
                clearActiveCall();
              },
            },
          ],
        });

      }

      // meeting 的 accepted（申請者被核准）你也可以在這裡做提示或自動開房
      // if (payload.kind === "meeting") { ... }
      if (payload.kind === "meeting") {
        // 申請者端：主持人核准 → 顯示通知 + 一鍵加入
        const notificationId = `meeting_accepted_${payload.id}_${Date.now()}`;
        const token = sessionStorage.getItem("flaskAccessToken");
        notifications.add({
          id: notificationId,
          type: "meeting_join_accepted",
          title: "已核准加入會議",
          message: payload.message || `你已被核准加入會議（ID: ${payload.id}）`,
          actions: [
            {
              label: "加入會議",
              onClick: async () => {
                const tokenRes = await fetch(`http://localhost:5000/api/rtc/token`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  credentials: "include",
                  body: JSON.stringify({ kind: "meeting", id: payload.id }),
                });
                const tokenData = await tokenRes.json();
                if (!tokenRes.ok) {
                  alert(tokenData.error || "取得 token 失敗");
                  return;
                }

                // TODO: 進入 Meeting 的 CallRoom UI
                // 你可以：
                // 1) setMeetingCallInfo({ token: tokenData.token, wsUrl: tokenData.ws_url, roomName: tokenData.room_name, mode: "audio" })
                // 2) 或 navigate(`/meetings/${payload.id}`) 並在 MeetingChat 裡根據某個 state 自動打開 CallRoom
              },
            },
          ],
        });

        setMeetingStatus(payload.id, "approved");
      }
    };

    // ---------------------------
    // rtc rejected (統一：meeting/private_call)
    // ---------------------------
    const handleRejected = (payload) => {
      console.log("收到 rtc:rejected:", payload);
      if (!payload?.kind || !payload?.id) return;

      if (payload.kind === "private_call") {
        notifications.add({
          id: `call_rejected_${payload.id}_${Date.now()}`,
          type: "private_call_rejected",
          title: "通話被拒絕",
          message: payload.message || "對方拒絕通話",
          actions: [],
        });
        // setCallStatus(payload.id, "rejected");

        notifications.remove(`call_${payload.id}`);
        clearActiveCall(); // ✅
        alert(payload.message || "對方拒絕通話");
        return;
      }
      // meeting rejected 也可以提示
      if (payload.kind === "meeting") {
        notifications.add({
          id: `meeting_rejected_${payload.id}_${Date.now()}`,
          type: "meeting_join_rejected",
          title: "加入會議被拒絕",
          message: payload.message || `主持人拒絕你加入會議（ID: ${payload.id}）`,
          actions: [],
        });
        setMeetingStatus(payload.id, "rejected");
      }
    };

    // 綁定事件
    socket.on("rtc:join_request", handleJoinRequest);
    socket.on("rtc:incoming", handleIncoming);
    socket.on("rtc:accepted", handleAccepted);
    socket.on("rtc:rejected", handleRejected);
    socket.on("rtc:ended", (payload) => {
      if (payload.kind !== "private_call") return;
      notifications.remove(`call_${payload.id}`);
      clearActiveCall();
      alert(payload.message || "通話已結束");
    });


    // cleanup：避免重複綁定造成觸發多次
    return () => {
      socket.off("rtc:join_request", handleJoinRequest);
      socket.off("rtc:incoming", handleIncoming);
      socket.off("rtc:accepted", handleAccepted);
      socket.off("rtc:rejected", handleRejected);
    };
  }, [socket, notifications, activeCall]);

  return null;
};

export default RTCNotificationsListener;
